import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { memo } from 'react';
import { Icon } from 'components';
import { Row } from 'components/layout';
import { USERINFO_LABELS } from 'hooks/useCandidateProfile';

import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';

import styles from '../../ProfileSection.common.module.scss';

interface RecommendedProfileSectionProps {
  onLabelClick: (label: USERINFO_LABELS) => void;
}

export const RecommendedProfileSection: React.FC<RecommendedProfileSectionProps> =
  memo(({ onLabelClick }: RecommendedProfileSectionProps) => {
    const [t] = useTranslation('candidate.profile');
    const { basicInfoComplete, educationComplete, experienceComplete } =
      useCandidateProfile();

    return (
      <section className={styles.recommendedSection}>
        <p className={styles.sectionTitle}>{t('sidebar.main.recommended')}</p>
        <button
          onClick={() => onLabelClick(USERINFO_LABELS.BASIC_INFO)}
          className={styles.sectionButton}
        >
          <Row
            align="center"
            justify="space-between"
            className={styles.sectionRow}
          >
            <Row
              align="center"
              className={cn(styles.sectionLabel, {
                [styles.sectionLabelComplete]: basicInfoComplete,
              })}
            >
              <Icon
                iconName="icon_profile"
                className={cn(styles.iconIncomplete, {
                  [styles.iconComplete]: basicInfoComplete,
                })}
              />
              <p>{t('sidebar.main.basic-info')}</p>
            </Row>
            {basicInfoComplete ? (
              <Icon
                key={1}
                iconName="tick-mark-circle"
                className={styles.tickCircleComplete}
              />
            ) : (
              <Icon
                key={2}
                iconName="icon_check"
                className={styles.tickCircleIncomplete}
              />
            )}
          </Row>
        </button>
        <button
          onClick={() => onLabelClick(USERINFO_LABELS.EDUCATION)}
          className={styles.sectionButton}
        >
          <Row
            align="center"
            justify="space-between"
            className={styles.sectionRow}
          >
            <Row
              align="center"
              className={cn(styles.sectionLabel, {
                [styles.sectionLabelComplete]: educationComplete,
              })}
            >
              <Icon
                iconName="icon_hat-university"
                className={cn(styles.iconIncomplete, {
                  [styles.iconComplete]: educationComplete,
                })}
              />
              <p>{t('sidebar.main.education')}</p>
            </Row>
            {educationComplete ? (
              <Icon
                key={1}
                iconName="tick-mark-circle"
                className={styles.tickCircleComplete}
              />
            ) : (
              <Icon
                key={2}
                iconName="icon_check"
                className={styles.tickCircleIncomplete}
              />
            )}
          </Row>
        </button>
        <button
          onClick={() => onLabelClick(USERINFO_LABELS.EXPERIENCE)}
          className={styles.sectionButton}
        >
          <Row
            align="center"
            justify="space-between"
            className={styles.sectionRow}
          >
            <Row
              align="center"
              className={cn(styles.sectionLabel, {
                [styles.sectionLabelComplete]: experienceComplete,
              })}
            >
              <Icon
                iconName="icon_briefcase"
                className={cn(styles.iconIncomplete, {
                  [styles.iconComplete]: experienceComplete,
                })}
              />
              <p>{t('sidebar.main.experience')}</p>
            </Row>
            {experienceComplete ? (
              <Icon
                key={1}
                iconName="tick-mark-circle"
                className={styles.tickCircleComplete}
              />
            ) : (
              <Icon
                key={2}
                iconName="icon_check"
                className={styles.tickCircleIncomplete}
              />
            )}
          </Row>
        </button>
      </section>
    );
  });

RecommendedProfileSection.displayName = 'RecommendedProfileSection';
